// The calendar's peek intent: hover shows a card's peek after a short delay,
// keyboard focus shows it at once, a click opens the post (never the peek),
// and touch has no hover, so a tap only opens. Pure and React-free, so
// scripts/test-calendar-month.mjs drives it with fake timers.

/** How long a pointer rests on a card before its peek shows. */
export const PEEK_INTENT_MS = 300;
/** How long the peek waits after the pointer leaves, so it can reach the peek. */
export const PEEK_CLOSE_MS = 200;

export interface PeekTimers {
  set: (run: () => void, ms: number) => number;
  clear: (id: number) => void;
}

export interface PeekController<P, A> {
  /** A mouse or pen came onto the card. */
  enter: (post: P, anchor: A) => void;
  leave: () => void;
  /** Keyboard focus on the card. */
  focus: (post: P, anchor: A) => void;
  /** Focus left the card; `intoPeek` when it moved into the peek itself. */
  blur: (intoPeek: boolean) => void;
  /** The pointer reached the peek: keep it open. */
  hold: () => void;
  dispose: () => void;
}

/**
 * The keyboard's way into and out of a peek, which never takes focus by
 * itself. Tab on a card whose peek is open goes into the peek; inside it,
 * Escape, or Shift+Tab from its first control, goes back to the card.
 */
export function peekKey({
  key,
  shift,
  where,
  peekOpenForCard = false,
  atFirst = false,
}: {
  key: string;
  shift: boolean;
  where: "card" | "peek";
  /** On a card: this card's peek is the one showing. */
  peekOpenForCard?: boolean;
  /** In the peek: focus is on its first control. */
  atFirst?: boolean;
}): "into-peek" | "to-card" | null {
  if (where === "card") return key === "Tab" && !shift && peekOpenForCard ? "into-peek" : null;
  if (key === "Escape") return "to-card";
  if (key === "Tab" && shift && atFirst) return "to-card";
  return null;
}

/** Hover and focus only: a touch pointer is left to the tap, which opens the post. */
export function hoverPointer(pointerType: string): boolean {
  return pointerType !== "touch";
}

export function createPeekController<P, A>({
  timers,
  show,
  close,
  modalOpen,
}: {
  timers: PeekTimers;
  show: (post: P, anchor: A) => void;
  /** Closes a hover or focus peek (never a day's list opened by a click). */
  close: () => void;
  /** A day's list, opened by a click, holds until it is closed. */
  modalOpen: () => boolean;
}): PeekController<P, A> {
  let hoverId: number | null = null;
  let closeId: number | null = null;
  const clearHover = () => {
    if (hoverId !== null) timers.clear(hoverId);
    hoverId = null;
  };
  const clearClose = () => {
    if (closeId !== null) timers.clear(closeId);
    closeId = null;
  };
  const clearAll = () => {
    clearHover();
    clearClose();
  };
  return {
    enter: (post, anchor) => {
      if (modalOpen()) return;
      clearAll();
      hoverId = timers.set(() => {
        hoverId = null;
        show(post, anchor);
      }, PEEK_INTENT_MS);
    },
    leave: () => {
      clearHover();
      clearClose();
      closeId = timers.set(() => {
        closeId = null;
        close();
      }, PEEK_CLOSE_MS);
    },
    focus: (post, anchor) => {
      if (modalOpen()) return;
      clearAll();
      show(post, anchor);
    },
    blur: (intoPeek) => {
      if (intoPeek) return;
      clearHover();
      close();
    },
    hold: clearClose,
    dispose: clearAll,
  };
}
